import { config } from '../../config/environment';

/**
 * What `PUT /users/me/picture` answers with.
 *
 * One field, because the upload's only job beyond storing the bytes is telling the caller
 * where they now live. The API returns a URL rather than 204 for a reason worth keeping in
 * mind before "tidying" it: the header avatar is on screen while the upload happens, and
 * this is what lets `updateUser` change it at once rather than on the next `GET /users/me`.
 */
export interface IUserPicture {
    /** Relative, and null-free -- an upload that succeeded always produced one. */
    pictureUrl: string;
}

/**
 * Anything the app can draw as a person: a name to fall back on and, maybe, a face.
 *
 * Deliberately structural rather than one of the eight interfaces that satisfy it. A patient,
 * a clinic member, a therapist on a booking form, the signed-in user and both parties to an
 * appointment are all "somebody to draw", and `UserAvatar` should not have to name them --
 * nor should adding a ninth mean editing it.
 */
export interface IPerson {
    firstName?: string | null;
    lastName?: string | null;
    /**
     * An already-joined display name, for the payloads that carry one instead of two columns.
     *
     * An appointment and a purchase both report the other party as a single string -- the
     * appointment because the name is pre-joined in SQL, the purchase because it is a
     * snapshot of who sold it. Neither can fill in `firstName`/`lastName` honestly, and
     * putting the whole name in `firstName` was the alternative. Wins over the pair when set.
     */
    name?: string | null;
    emailAddress?: string | null;
    pictureUrl?: string | null;
}

/**
 * The API's relative path made absolute against wherever the API lives.
 *
 * The sibling of `absoluteBrandingUrl`, minus its `?v={etag}` cache-buster -- and the absence
 * is the point rather than an omission. A logo's path is stable for the life of a clinic and
 * its response is cached for five minutes, so a re-crop stays invisible without one. A
 * picture's path carries a token that is minted fresh on every upload, so a new picture is
 * already a new URL and the old one simply stops resolving.
 *
 * Relative in the API and absolute only here, because a relative `/api` resolves against
 * whatever hostname the app was loaded from -- which is what lets one static build serve
 * every clinic.
 */
export const absoluteUserPictureUrl = (
    pictureUrl: string | null | undefined
): string | null => (pictureUrl ? `${config.apiBaseUrl}${pictureUrl}` : null);

/**
 * The letter drawn when there is no picture, which is most people most of the time.
 *
 * The email address is the fallback behind the name because a patient can be invited, and
 * booked in, long before they claim their account and supply one -- the same reason
 * `ToMapper.displayName` falls back to it on the server.
 */
export const personInitial = (person: IPerson | null | undefined): string =>
    person?.name?.trim()?.charAt(0)?.toUpperCase()
    || person?.firstName?.charAt(0)?.toUpperCase()
    || person?.lastName?.charAt(0)?.toUpperCase()
    || person?.emailAddress?.charAt(0)?.toUpperCase()
    || 'U';
